<template>
  <v-card flat class="pa-4">
    <div class="text-caption text-uppercase primary--text font-weight-bold mb-1">Step 3 of 3</div>
    <v-card-title class="text-h4 font-weight-black mb-2 pa-0 text--primary">
      Choose Layout &amp; Theme
    </v-card-title>
    <v-card-subtitle class="pa-0 mb-8 text-subtitle-1">
      Pick a starting layout and a color theme. You can completely change this later in the editor!
    </v-card-subtitle>

    <v-card-text class="pa-0">
      <div class="font-weight-bold mb-3 text-subtitle-1 text--primary">Starting Layout</div>
      <v-row class="mb-8" dense>
        <v-col
          v-for="layout in layouts"
          :key="layout.id"
          cols="12"
          sm="4"
        >
          <v-card
            outlined
            :class="{ 'primary--border': selectedLayout === layout.id }"
            :color="selectedLayout === layout.id ? 'primary lighten-5' : ''"
            class="text-center pa-4 cursor-pointer transition-fast"
            style="height: 100%"
            @click="selectedLayout = layout.id"
          >
            <v-icon size="48" :color="selectedLayout === layout.id ? 'primary' : 'grey darken-1'" class="mb-2">
              {{ layout.icon }}
            </v-icon>
            <div class="font-weight-bold text-subtitle-1" :class="selectedLayout === layout.id ? 'primary--text' : ''">{{ layout.label }}</div>
            <div class="text-caption grey--text text--darken-1">{{ layout.description }}</div>
            <v-icon
              v-if="selectedLayout === layout.id"
              color="primary"
              class="check-icon"
            >
              mdi-check-circle
            </v-icon>
          </v-card>
        </v-col>
      </v-row>

      <div class="font-weight-bold mb-3 text-subtitle-1 text--primary">Color Theme</div>
      <v-row dense class="mb-4">
        <v-col v-for="(t, idx) in themePresets" :key="idx" cols="6" sm="3">
          <v-card 
            outlined 
            class="cursor-pointer transition-fast"
            :class="{ 'primary--border': selectedTheme === t.name }"
            @click="selectedTheme = t.name"
          >
            <div :style="{background: t.background, height: '60px', 'border-radius': '4px 4px 0 0', position: 'relative'}" class="d-flex align-center justify-center">
              <div :style="{background: t.primary, width: '24px', height: '24px', borderRadius: t.radius}"></div>
              <v-icon v-if="selectedTheme === t.name" color="success" class="check-icon-theme">mdi-check-circle</v-icon>
            </div>
            <div class="text-caption text-center py-2 font-weight-bold" :class="selectedTheme === t.name ? 'primary--text' : ''">{{ t.name }}</div>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="pa-0 mt-8">
      <v-btn class="customBorderRadius px-6 font-weight-bold" text large @click="$emit('back')">
        <v-icon left>mdi-arrow-left</v-icon>
        Back
      </v-btn>
      <v-spacer />
      <v-btn class="customBorderRadius px-8 font-weight-bold"
        color="primary"
        large
        depressed
        :disabled="!isValid"
        @click="generate"
      >
        <v-icon left>mdi-auto-fix</v-icon>
        Generate My Page
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "WizardStep3",
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedLayout: this.value.layoutId || "split",
      selectedTheme: (this.value.colorScheme && this.value.colorScheme.preset) || "Corporate",
      layouts: [
        {
          id: "hero",
          label: "Hero Model",
          description: "Full-width hero header leading to your forms",
          icon: "mdi-image-area",
        },
        {
          id: "split",
          label: "Split Focus",
          description: "Side-by-side content and sticky form",
          icon: "mdi-page-layout-sidebar-right",
        },
        {
          id: "minimal",
          label: "Minimalist",
          description: "Clean, direct forms without bulk",
          icon: "mdi-text-box-outline",
        },
      ],
      themePresets: [
        { name: 'Minimal', primary: '#18181b', secondary: '#09090b', background: '#ffffff', textColor: '#27272a', textSecondary: '#71717a', font: 'Inter', radius: '0px', isDark: false, preset: 'Minimal' },
        { name: 'Corporate', primary: '#2563eb', secondary: '#1d4ed8', background: '#ffffff', textColor: '#1e293b', textSecondary: '#64748b', font: 'Roboto', radius: '4px', isDark: false, preset: 'Corporate' },
        { name: 'Modern AI', primary: '#6d28d9', secondary: '#4c1d95', background: '#0f172a', textColor: '#f8fafc', textSecondary: '#94a3b8', font: 'Inter', radius: '12px', isDark: true, preset: 'Modern AI' },
        { name: 'Startup Pop', primary: '#f43f5e', secondary: '#e11d48', background: '#fff1f2', textColor: '#334155', textSecondary: '#64748b', font: 'Poppins', radius: '24px', isDark: false, preset: 'Startup Pop' }
      ],
    };
  },
  computed: {
    isValid() {
      return this.selectedLayout && this.selectedTheme;
    },
    chosenTheme() {
      return this.themePresets.find((c) => c.name === this.selectedTheme);
    },
  },
  methods: {
    generate() {
      if (!this.isValid) return;
      this.$emit("input", {
        ...this.value,
        layoutId: this.selectedLayout,
        colorScheme: this.chosenTheme,
      });
      this.$emit("generate");
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.transition-fast {
  transition: all 0.2s ease-in-out;
}
.transition-fast:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
}
.primary--border {
  border: 2px solid var(--v-primary-base) !important;
}
.check-icon {
  position: absolute;
  top: 8px;
  right: 8px;
}
.check-icon-theme {
  position: absolute;
  top: 4px;
  right: 4px;
  background: white;
  border-radius: 50%;
}
</style>